import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Headphones, Pause, Play, Square } from 'lucide-react';

// listen_bar_under_header_jun2026 (CEO): Listen / Pause / Stop bar sits under
// the header only. The floating "Listen to this page" pill is removed everywhere.
const chunkPageText = () => {
  const root = document.querySelector('main') || document.body;
  const raw = (root.innerText || '').replace(/\s+/g, ' ').trim();
  if (!raw) return [];
  const sentences = raw.match(/[^.!?]+[.!?]*/g) || [raw];
  const parts = [];
  let cur = '';
  sentences.forEach((s) => {
    if ((cur + s).length > 220 && cur) { parts.push(cur.trim()); cur = s; }
    else cur += s;
  });
  if (cur.trim()) parts.push(cur.trim());
  return parts.slice(0, 120);
};

const pill = {
  height: 44, padding: '0 18px', borderRadius: 22, border: '1px solid #DCCFC6',
  background: '#FFFFFF', fontWeight: 500, display: 'inline-flex', alignItems: 'center',
  gap: 8, fontFamily: "'Lexend','Helvetica Neue',Arial,sans-serif", fontSize: 15,
  color: '#2B1F27', cursor: 'pointer',
};

export default function ListenBar() {
  const location = useLocation();
  const [playing, setPlaying] = useState(false);
  const [parts, setParts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const playingRef = useRef(false);
  const idxRef = useRef(0);

  const stopAll = () => {
    playingRef.current = false;
    try { window.speechSynthesis.cancel(); } catch (e) { /* unsupported */ }
    setPlaying(false); setPaused(false); setIdx(0); idxRef.current = 0;
  };

  useEffect(() => { stopAll(); }, [location.pathname]); // eslint-disable-line

  const speakFrom = (list, i) => {
    if (!list[i]) { stopAll(); return; }
    idxRef.current = i; setIdx(i);
    const u = new SpeechSynthesisUtterance(list[i]);
    u.rate = 0.98; u.lang = 'en-GB';
    u.onend = () => { if (playingRef.current) speakFrom(list, idxRef.current + 1); };
    try { window.speechSynthesis.speak(u); } catch (e) { /* unsupported */ }
  };

  const start = () => {
    const list = chunkPageText();
    if (!list.length) return;
    try { window.speechSynthesis.cancel(); } catch (e) { /* noop */ }
    setParts(list); setPlaying(true); setPaused(false);
    playingRef.current = true;
    speakFrom(list, 0);
  };
  const pauseResume = () => {
    if (!playing) return;
    if (paused) { try { window.speechSynthesis.resume(); } catch (e) {} setPaused(false); }
    else { try { window.speechSynthesis.pause(); } catch (e) {} setPaused(true); }
  };

  return (
    <div data-testid="listen-bar" className="hidden xl:flex" style={{ gap: 8, justifyContent: 'flex-end', alignItems: 'center', padding: '0 0 10px', flexWrap: 'wrap' }}>
      {playing && (
        <span data-testid="listen-part-label" style={{ color: '#7E5F2E', fontSize: 12, fontFamily: 'Lexend, sans-serif' }}>
          Part {idx + 1} of {parts.length}
        </span>
      )}
      <button data-testid="listen-launch-btn" onClick={start} aria-label="Listen to this page"
        style={{ ...pill, background: '#291525', color: '#F8F4F1', borderColor: '#291525' }}>
        <Headphones size={16} /><span className="hidden sm:inline">Listen to this page</span><span className="sm:hidden">Listen</span>
      </button>
      <button data-testid="listen-pause-btn" onClick={pauseResume} aria-label={paused ? 'Resume' : 'Pause'} style={pill}>
        {paused ? <Play size={15} /> : <Pause size={15} />}<span className="hidden sm:inline">{paused ? 'Resume' : 'Pause'}</span>
      </button>
      <button data-testid="listen-stop-btn" onClick={stopAll} aria-label="Stop" style={pill}>
        <Square size={14} /><span className="hidden sm:inline">Stop</span>
      </button>
    </div>
  );
}
